import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { BuildingRel } from './geojson'
import { nextFetchArea, type GeoBounds } from './viewport'

export type LayerKey = 'buildings' | 'givers' | 'police' | 'roamers' | 'crew' | 'heat'

/**
 * What the tilt control is set to.
 *
 * `auto` exists because the 3D buildings and the tilt are one decision rather
 * than two: at pitch 0 a model renders as a ROOFTOP, so there is nothing to
 * see until the camera leans over, and no reason for it to lean over until
 * there is something with a facade on it. `renderPlan.tiltFor` is the rule.
 */
export type TiltMode = 'auto' | 'flat' | 'tilted'

export const TILT_MODES: readonly TiltMode[] = ['auto', 'flat', 'tilted']

/** The next mode when the chrome button is pressed. */
export function nextTiltMode(mode: TiltMode): TiltMode {
  return TILT_MODES[(TILT_MODES.indexOf(mode) + 1) % TILT_MODES.length]
}

export const LAYER_KEYS: readonly LayerKey[] = ['buildings', 'givers', 'police', 'roamers', 'crew', 'heat']
export const BUILDING_RELS: readonly BuildingRel[] = ['mine', 'faction', 'rival', 'unclaimed']

export interface CameraState {
  lng: number
  lat: number
  zoom: number
}

interface MapState {
  /** The visible bounds after the last move. */
  viewport: GeoBounds | null
  zoom: number
  /**
   * The camera's live pitch. Published by `MapView` rather than derived from
   * `tiltMode`, because `applyTilt` eases over 600 ms and the art swap has to
   * follow the camera, not the intent — see `renderPlan.buildingArt`.
   */
  pitch: number
  /** The ONE area every feed fetches (padded + quantised viewport). */
  fetchArea: GeoBounds | null
  /** Last camera, so returning to /map resumes where the player left off. */
  camera: CameraState | null
  tiltMode: TiltMode
  /** The player's toggles ("Enabled"). The server's verdict ("Available") is separate. */
  enabled: Record<LayerKey, boolean>
  buildingFilter: Record<BuildingRel, boolean>
  setView: (viewport: GeoBounds, zoom: number, camera: CameraState) => void
  setTiltMode: (mode: TiltMode) => void
  setPitch: (pitch: number) => void
  toggleLayer: (key: LayerKey) => void
  toggleRel: (rel: BuildingRel) => void
  reset: () => void
}

const defaultEnabled: Record<LayerKey, boolean> = {
  buildings: true,
  givers: true,
  police: true,
  roamers: true,
  crew: true,
  heat: true,
}
const defaultFilter: Record<BuildingRel, boolean> = { mine: true, faction: true, rival: true, unclaimed: true }

export const useMapStore = create<MapState>()(
  persist(
    (set) => ({
      viewport: null,
      zoom: 15,
      pitch: 0,
      fetchArea: null,
      camera: null,
      tiltMode: 'auto',
      enabled: defaultEnabled,
      buildingFilter: defaultFilter,
      setView: (viewport, zoom, camera) =>
        set((s) => ({ viewport, zoom, camera, fetchArea: nextFetchArea(viewport, zoom, s.fetchArea) })),
      setTiltMode: (tiltMode) => set({ tiltMode }),
      setPitch: (pitch) => set({ pitch }),
      toggleLayer: (key) => set((s) => ({ enabled: { ...s.enabled, [key]: !s.enabled[key] } })),
      toggleRel: (rel) => set((s) => ({ buildingFilter: { ...s.buildingFilter, [rel]: !s.buildingFilter[rel] } })),
      // Camera only. tiltMode is NOT reset: clearLocal calls this on sign-out
      // and tiltMode is a persisted per-viewer preference like `enabled` and
      // `buildingFilter`, which this deliberately leaves alone — resetting it
      // would flush the discard to localStorage and lose an explicit choice.
      reset: () => set({ viewport: null, fetchArea: null, camera: null, pitch: 0 }),
    }),
    {
      // Per-viewer conveniences only (layer toggles). Never anything that
      // must persist reliably — storage can be empty or throw.
      name: 'mafiago.map-prefs',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ enabled: s.enabled, buildingFilter: s.buildingFilter, tiltMode: s.tiltMode }),
      // 2: tiltMode joined the persisted set. A returning viewer's record has
      // no such key, and an undefined mode would read as neither 'auto' nor
      // 'flat' — so it is filled in rather than left to a `??` at every use.
      version: 2,
      migrate: (persisted, from) => {
        const prior = (persisted ?? {}) as Partial<{ tiltMode: TiltMode }>
        return from < 2 ? { ...prior, tiltMode: prior.tiltMode ?? 'auto' } : prior
      },
    },
  ),
)
